/**
 * @module app.shared
 */

import { CustomAttributeBase } from 'app/shared/attributes/abstracts/custom-attribute-base';
import { getClosestBoundary } from 'app/shared/lib/dom';
import { inject, bindable, bindingMode } from 'aurelia-framework';

function parseAsCss(str: string | number): string {
    let num = str;
    if(typeof (num) === 'string') {
        num = parseFloat(num);
    }
    return isNaN(num) ? str as string : `${num}px`;
}

const BOTTOM_OFFSET_VARIABLE = '--ess-scrollbar-bottom';

abstract class PositionCustomAttribute extends CustomAttributeBase {

    top: number | string | null = null;
    right: number | string | null = null;
    bottom: number | string | null = null;
    left: number | string | null = null;
    enabled: boolean = true;

    protected className!: string;

    bind() {
        this.enabledChanged();
        if(this.top !== null) {
            this.element.style.top = parseAsCss(this.top);
        }
        if(this.right !== null) {
            this.element.style.right = parseAsCss(this.right);
        }
        if(this.bottom !== null) {
            this.element.style.bottom = parseAsCss(this.bottom);
        }
        if(this.left !== null) {
            this.element.style.left = parseAsCss(this.left);
        }
    }

    protected enabledChanged() {
        this.element.classList.toggle(this.className, this.enabled);
    }
}

@inject(Element)
@bindable({ name: 'top', primaryProperty: true, defaultBindingMode: bindingMode.oneTime })
@bindable({ name: 'right', defaultBindingMode: bindingMode.oneTime })
@bindable({ name: 'bottom', defaultBindingMode: bindingMode.oneTime })
@bindable({ name: 'left', defaultBindingMode: bindingMode.oneTime })
@bindable({ name: 'enabled', defaultBindingMode: bindingMode.toView })
export class EssStickyCustomAttribute extends PositionCustomAttribute {

    protected root!: HTMLElement;
    protected className = 'ess-sticky';

    protected attached() {
        this.root = getClosestBoundary(this.element);
        if(this.bottom !== null) {
            let bottom = `${this.element.getBoundingClientRect().height}px`;
            this.root.style.setProperty(BOTTOM_OFFSET_VARIABLE, bottom);
        }
    }

    protected detached() {
        this.root.style.removeProperty(BOTTOM_OFFSET_VARIABLE);
    }
}

@inject(Element)
@bindable({ name: 'top', primaryProperty: true, defaultBindingMode: bindingMode.oneTime })
@bindable({ name: 'right', defaultBindingMode: bindingMode.oneTime })
@bindable({ name: 'bottom', defaultBindingMode: bindingMode.oneTime })
@bindable({ name: 'left', defaultBindingMode: bindingMode.oneTime })
@bindable({ name: 'enabled', defaultBindingMode: bindingMode.toView })
export class EssFixedCustomAttribute extends PositionCustomAttribute {
    className = 'ess-fixed';

    protected attached() {
        if(this.bottom !== null) {
            let bottom = `${this.element.getBoundingClientRect().height}px`;
            if(String(this.bottom) !== '0') {
                bottom = `calc(${this.bottom} + ${bottom})`;
            }
            const parent = this.element.parentElement;
            if(parent) {
                parent.style.setProperty('padding-bottom', bottom);
            }
        }
    }
}
